import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Button,
  Badge,
  Form,
  Modal,
  Alert,
  Spinner,
  ButtonGroup,
  InputGroup,
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import Layout from '../components/layouts/Main';
import config from '../config';
import {
  FiPlus,
  FiSearch,
  FiFilter,
  FiRefreshCw,
  FiEye,
  FiCheck,
  FiX,
  FiClock,
  FiPackage,
  FiTrendingUp,
  FiAlertCircle,
  FiUsers,
  FiShoppingCart,
  FiTarget
} from 'react-icons/fi';

const PurchaseRequestManagement = () => {
  const navigate = useNavigate();

  // State management
  const [purchaseRequests, setPurchaseRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    consolidation_status: '',
    has_rfq: '',
    department: '',
    date_from: '',
    date_to: '',
    search: ''
  });
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_count: 0,
    per_page: 20
  });
  const [summary, setSummary] = useState({});
  const [activeTab, setActiveTab] = useState('all');
  
  // Modal states
  const [showConsolidationModal, setShowConsolidationModal] = useState(false);
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [showBulkUpdateModal, setShowBulkUpdateModal] = useState(false);
  const [consolidationAnalysis, setConsolidationAnalysis] = useState([]);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [selectedRequests, setSelectedRequests] = useState([]);
  const [bulkUpdateAction, setBulkUpdateAction] = useState('');

  const fetchPurchaseRequests = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.current_page,
        limit: pagination.per_page,
        ...filters
      });

      // Add tab-specific filters
      if (activeTab !== 'all') {
        if (activeTab === 'approved') {
          params.set('status', 'approved');
        } else if (activeTab === 'consolidation_eligible') {
          params.set('consolidation_eligible', 'true');
          params.set('consolidation_status', 'eligible');
        } else if (activeTab === 'with_rfq') {
          params.set('has_rfq', 'true');
        } else if (activeTab === 'pending_approval') {
          params.set('status', 'requested');
        }
      }

      const response = await axios.get(`${config.API_BASE_URL}/purchase-requests?${params}`);
      
      setPurchaseRequests(response.data.requests || []);
      setPagination(response.data.pagination || {});
      setSummary(response.data.summary || {});
      
    } catch (error) {
      console.error('Error fetching purchase requests:', error);
      toast.error('Failed to fetch purchase requests');
    } finally {
      setLoading(false);
    }
  },[filters, pagination.current_page, pagination.per_page, activeTab]);
  
  useEffect(() => {
    fetchPurchaseRequests();
  }, [filters, pagination.current_page, activeTab, fetchPurchaseRequests]);

  const fetchConsolidationAnalysis = async (groupBy = 'product') => {
    try {
      setLoadingAnalysis(true);
      const response = await axios.get(`${config.API_BASE_URL}/purchase-requests/consolidation/analysis?group_by=${groupBy}`);
      setConsolidationAnalysis(response.data.analysis || []);
    } catch (error) {
      console.error('Error fetching consolidation analysis:', error);
      toast.error('Failed to fetch consolidation analysis');
    } finally {
      setLoadingAnalysis(false);
    }
  };

  const handleBulkConsolidationUpdate = async () => {
    if (selectedRequests.length === 0 || !bulkUpdateAction) {
      toast.error('Please select requests and an action');
      return;
    }

    try {
      const response = await axios.post(`${config.API_BASE_URL}/purchase-requests/consolidation/bulk-update`, {
        purchase_request_ids: selectedRequests,
        consolidation_status: bulkUpdateAction,
        notes: `Bulk updated to ${bulkUpdateAction} status`
      });

      if (response.data.success) {
        toast.success(`Updated ${response.data.updated_count} purchase requests`);
        setShowBulkUpdateModal(false);
        setSelectedRequests([]);
        setBulkUpdateAction('');
        fetchPurchaseRequests();
      }
    } catch (error) {
      console.error('Error in bulk update:', error);
      toast.error('Failed to update requests');
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      priority: '',
      consolidation_status: '',
      has_rfq: '',
      department: '',
      date_from: '',
      date_to: '',
      search: ''
    });
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'requested': { bg: 'warning', text: 'Requested' },
      'approved': { bg: 'success', text: 'Approved' },
      'rejected': { bg: 'danger', text: 'Rejected' },
      'processing': { bg: 'info', text: 'Processing' },
      'rfq_generated': { bg: 'primary', text: 'RFQ Generated' },
      'completed': { bg: 'dark', text: 'Completed' }
    };
    const statusInfo = statusMap[status] || { bg: 'secondary', text: status };
    return <Badge bg={statusInfo.bg}>{statusInfo.text}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const priorityMap = {
      'low': { bg: 'light', text: 'Low', textColor: 'dark' },
      'medium': { bg: 'info', text: 'Medium' },
      'high': { bg: 'warning', text: 'High' },
      'urgent': { bg: 'danger', text: 'Urgent' }
    };
    const priorityInfo = priorityMap[priority] || { bg: 'secondary', text: priority };
    return (
      <Badge bg={priorityInfo.bg} text={priorityInfo.textColor}>
        {priorityInfo.text}
      </Badge>
    );
  };

  const getConsolidationStatusBadge = (status) => {
    const statusMap = {
      'eligible': { bg: 'success', text: 'Eligible', icon: <FiTarget /> },
      'consolidated': { bg: 'primary', text: 'Consolidated', icon: <FiTrendingUp /> },
      'excluded': { bg: 'danger', text: 'Excluded', icon: <FiX /> },
      'individual': { bg: 'info', text: 'Individual', icon: <FiPackage /> }
    };
    const statusInfo = statusMap[status] || { bg: 'secondary', text: status, icon: null };
    return (
      <Badge bg={statusInfo.bg}>
        {statusInfo.icon} {statusInfo.text}
      </Badge>
    );
  };

  const renderPurchaseRequestTable = () => (
    <div className="table-responsive">
      <Table hover>
        <thead className="bg-light">
          <tr>
            <th>
              <Form.Check
                type="checkbox"
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedRequests(purchaseRequests.map(pr => pr._id));
                  } else {
                    setSelectedRequests([]);
                  }
                }}
                checked={selectedRequests.length === purchaseRequests.length && purchaseRequests.length > 0}
              />
            </th>
            <th>Request #</th>
            <th>Priority</th>
            <th>Items</th>
            <th>Status</th>
            <th>Consolidation</th>
            <th>RFQ Status</th>
            <th>Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {purchaseRequests.length === 0 ? (
            <tr>
              <td colSpan="9" className="text-center py-4">
                <div className="text-muted">
                  {loading ? 'Loading purchase requests...' : 'No purchase requests found'}
                </div>
              </td>
            </tr>
          ) : (
            purchaseRequests.map((pr) => (
              <tr key={pr._id}>
                <td>
                  <Form.Check
                    type="checkbox"
                    checked={selectedRequests.includes(pr._id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedRequests(prev => [...prev, pr._id]);
                      } else {
                        setSelectedRequests(prev => prev.filter(id => id !== pr._id));
                      }
                    }}
                  />
                </td>
                <td>
                  <div>
                    <strong className="text-primary">{pr.request_no}</strong>
                    {pr.is_overdue && (
                      <div><Badge bg="danger" size="sm">Overdue</Badge></div>
                    )}
                    {pr.soId && (
                      <div>
                        <small className="text-info">
                          SO: {pr.soId.orderNumber}
                        </small>
                      </div>
                    )}
                  </div>
                </td>
                <td>{getPriorityBadge(pr.priority)}</td>
                <td>
                  <div>
                    <FiPackage className="me-1" />
                    <strong>{pr.total_items || 0}</strong> items
                    <div>
                      <small className="text-muted">
                        {pr.unique_products || 0} unique products
                      </small>
                    </div>
                    {pr.total_estimated_value > 0 && (
                      <div>
                        <small className="text-success">
                          ₹{pr.total_estimated_value.toLocaleString()}
                        </small>
                      </div>
                    )}
                  </div>
                </td>
                <td>{getStatusBadge(pr.status)}</td>
                <td>
                  <div>
                    {getConsolidationStatusBadge(pr.consolidation_status)}
                    {pr.consolidation_opportunity && (
                      <div>
                        <Badge bg="warning" size="sm">
                          <FiTrendingUp className="me-1" />
                          Opportunity
                        </Badge>
                      </div>
                    )}
                  </div>
                </td>
                <td>
                  {pr.has_rfq ? (
                    <div>
                      <Badge bg="success">
                        <FiCheck className="me-1" />
                        {pr.rfq_count} RFQ{pr.rfq_count > 1 ? 's' : ''}
                      </Badge>
                      <div>
                        {pr.rfq_ids?.map((rfq, idx) => (
                          <small key={idx} className="text-muted d-block">
                            {rfq.rfq_id} - {rfq.status}
                          </small>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <Badge bg="light" text="dark">
                      <FiX className="me-1" />
                      No RFQ
                    </Badge>
                  )}
                </td>
                <td>
                  <div>
                    {new Date(pr.created_at).toLocaleDateString()}
                    <div>
                      <small className="text-muted">
                        {pr.days_since_created} days ago
                      </small>
                    </div>
                    {pr.requested_by && (
                      <div>
                        <small className="text-info">
                          by {pr.requested_by.name}
                        </small>
                      </div>
                    )}
                  </div>
                </td>
                <td>
                  <ButtonGroup size="sm">
                    <Button
                      variant="outline-primary"
                      onClick={() => navigate(`/purchase-requests/${pr._id}`)}
                      title="View Details"
                    >
                      <FiEye />
                    </Button>
                    {pr.status === 'requested' && (
                      <Button
                        variant="outline-success"
                        onClick={() => handleApproveRequest(pr._id)}
                        title="Approve"
                      >
                        <FiCheck />
                      </Button>
                    )}
                    {pr.status === 'approved' && !pr.has_rfq && (
                      <Button
                        variant="outline-warning"
                        onClick={() => navigate(`/rfqs/create?pr_id=${pr._id}`)}
                        title="Create RFQ"
                      >
                        <FiShoppingCart />
                      </Button>
                    )}
                  </ButtonGroup>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
    </div>
  );

  const handleApproveRequest = async (requestId) => {
    try {
      const response = await axios.put(`${config.API_BASE_URL}/purchase-requests/${requestId}`, {
        status: 'approved',
        approved_by: 'current-user-id', // Replace with actual user ID
        approved_at: new Date()
      });
      
      if (response.data) {
        toast.success('Purchase request approved successfully');
        fetchPurchaseRequests();
      }
    } catch (error) {
      console.error('Error approving request:', error);
      toast.error('Failed to approve request');
    }
  };

  const renderConsolidationModal = () => (
    <Modal show={showConsolidationModal} onHide={() => setShowConsolidationModal(false)} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>
          <FiTrendingUp className="me-2" />
          Consolidation Analysis
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <ButtonGroup>
            <Button
              variant={loadingAnalysis ? "outline-primary" : "primary"}
              onClick={() => fetchConsolidationAnalysis('product')}
              disabled={loadingAnalysis}
            >
              Group by Product
            </Button>
            <Button
              variant="outline-primary"
              onClick={() => fetchConsolidationAnalysis('delivery_location')}
              disabled={loadingAnalysis}
            >
              Group by Location
            </Button>
            <Button
              variant="outline-primary"
              onClick={() => fetchConsolidationAnalysis('date')}
              disabled={loadingAnalysis}
            >
              Group by Date
            </Button>
          </ButtonGroup>
        </div>

        {loadingAnalysis ? (
          <div className="text-center py-4">
            <Spinner animation="border" />
            <p className="mt-2">Analyzing consolidation opportunities...</p>
          </div>
        ) : (
          <>
            {consolidationAnalysis.length === 0 ? (
              <Alert variant="info">
                <FiAlertCircle className="me-2" />
                No consolidation opportunities found with current criteria.
              </Alert>
            ) : (
              <div>
                <Alert variant="success">
                  <strong>{consolidationAnalysis.length}</strong> consolidation opportunities identified!
                  Potential savings: <strong>₹{consolidationAnalysis.reduce((sum, opp) => sum + opp.potential_savings.estimated_money_saved, 0).toLocaleString()}</strong>
                </Alert>
                
                {consolidationAnalysis.slice(0, 10).map((opportunity, index) => (
                  <Card key={index} className="mb-3">
                    <Card.Header>
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <h6 className="mb-0">
                            {opportunity.product?.productName || `Consolidation Group ${index + 1}`}
                            <Badge bg="info" className="ms-2">
                              Score: {opportunity.consolidation_score.toFixed(0)}
                            </Badge>
                          </h6>
                          <small className="text-muted">
                            {opportunity.purchase_request_count} Purchase Requests • 
                            Total Value: ₹{opportunity.totalValue?.toLocaleString()} • 
                            Priority: {opportunity.priorities.join(', ')}
                          </small>
                        </div>
                        <Button
                          variant="success"
                          size="sm"
                          onClick={() => navigate(`/rfqs/create-consolidated?opportunity=${JSON.stringify(opportunity)}`)}
                        >
                          Create Consolidated RFQ
                        </Button>
                      </div>
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        <Col md={8}>
                          <h6>Purchase Requests ({opportunity.purchase_request_count}):</h6>
                          <div className="mb-3">
                            {opportunity.items.slice(0, 5).map((item, idx) => (
                              <Badge key={idx} bg="light" text="dark" className="me-2 mb-1">
                                {item.purchaseRequestNo}: {item.quantity} {item.unit}
                              </Badge>
                            ))}
                            {opportunity.items.length > 5 && (
                              <Badge bg="secondary">+{opportunity.items.length - 5} more</Badge>
                            )}
                          </div>

                          <div className="row">
                            <div className="col-md-6">
                              <h6>Delivery Timeline:</h6>
                              <div className="text-muted">
                                Spread: {opportunity.date_spread_days} days
                                <br />
                                Locations: {opportunity.deliveryLocations.join(', ') || 'Various'}
                              </div>
                            </div>
                            <div className="col-md-6">
                              <h6>Risk Assessment:</h6>
                              <Badge bg={opportunity.risk_level === 'Low' ? 'success' : 'warning'}>
                                {opportunity.risk_level} Risk
                              </Badge>
                              <div className="text-muted">
                                {opportunity.recommendation}
                              </div>
                            </div>
                          </div>
                        </Col>
                        <Col md={4}>
                          <div className="bg-light p-3 rounded">
                            <h6>Potential Savings</h6>
                            <div className="mb-2">
                              <strong>Cost Savings:</strong>
                              <div className="text-success">
                                ₹{opportunity.potential_savings.estimated_money_saved.toLocaleString()}
                                ({opportunity.potential_savings.cost_percentage_saved}%)
                              </div>
                            </div>
                            <div className="mb-2">
                              <strong>Time Saved:</strong>
                              <div className="text-info">
                                {opportunity.potential_savings.admin_hours_saved} hours
                              </div>
                            </div>
                            <div>
                              <strong>Efficiency:</strong>
                              <div className="text-primary">
                                {opportunity.potential_savings.efficiency_gain}
                              </div>
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowConsolidationModal(false)}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );

  const renderBulkUpdateModal = () => (
    <Modal show={showBulkUpdateModal} onHide={() => setShowBulkUpdateModal(false)}>
      <Modal.Header closeButton>
        <Modal.Title>
          Bulk Update Consolidation Status
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="info">
          You have selected <strong>{selectedRequests.length}</strong> purchase requests.
        </Alert>
        
        <Form.Group>
          <Form.Label>New Consolidation Status</Form.Label>
          <Form.Select
            value={bulkUpdateAction}
            onChange={(e) => setBulkUpdateAction(e.target.value)}
          >
            <option value="">Select action...</option>
            <option value="eligible">Mark as Eligible</option>
            <option value="excluded">Exclude from Consolidation</option>
            <option value="individual">Process Individually</option>
          </Form.Select>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowBulkUpdateModal(false)}>
          Cancel
        </Button>
        <Button 
          variant="primary" 
          onClick={handleBulkConsolidationUpdate}
          disabled={!bulkUpdateAction}
        >
          Update {selectedRequests.length} Requests
        </Button>
      </Modal.Footer>
    </Modal>
  );

  const renderFiltersModal = () => (
    <Modal show={showFiltersModal} onHide={() => setShowFiltersModal(false)}>
      <Modal.Header closeButton>
        <Modal.Title>
          <FiFilter className="me-2" />
          Advanced Filters
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Status</Form.Label>
              <Form.Select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">All Statuses</option>
                <option value="requested">Requested</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="processing">Processing</option>
                <option value="rfq_generated">RFQ Generated</option>
                <option value="completed">Completed</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Priority</Form.Label>
              <Form.Select
                value={filters.priority}
                onChange={(e) => handleFilterChange('priority', e.target.value)}
              >
                <option value="">All Priorities</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Consolidation Status</Form.Label>
              <Form.Select
                value={filters.consolidation_status}
                onChange={(e) => handleFilterChange('consolidation_status', e.target.value)}
              >
                <option value="">All Consolidation Status</option>
                <option value="eligible">Eligible</option>
                <option value="consolidated">Consolidated</option>
                <option value="excluded">Excluded</option>
                <option value="individual">Individual</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>RFQ Status</Form.Label>
              <Form.Select
                value={filters.has_rfq}
                onChange={(e) => handleFilterChange('has_rfq', e.target.value)}
              >
                <option value="">All</option>
                <option value="true">With RFQ</option>
                <option value="false">Without RFQ</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Date From</Form.Label>
              <Form.Control
                type="date"
                value={filters.date_from}
                onChange={(e) => handleFilterChange('date_from', e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Date To</Form.Label>
              <Form.Control
                type="date"
                value={filters.date_to}
                onChange={(e) => handleFilterChange('date_to', e.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={clearFilters}>
          Clear All
        </Button>
        <Button variant="secondary" onClick={() => setShowFiltersModal(false)}>
          Close
        </Button>
        <Button variant="primary" onClick={() => setShowFiltersModal(false)}>
          Apply Filters
        </Button>
      </Modal.Footer>
    </Modal>
  );

  const renderSummaryCards = () => (
    <Row className="mb-4">
      <Col md={2}>
        <Card className="border-0 shadow-sm">
          <Card.Body className="text-center">
            <div className="text-primary mb-2">
              <FiPackage size={20} />
            </div>
            <h5 className="text-primary">{summary.total_requests || 0}</h5>
            <small className="text-muted">Total Requests</small>
          </Card.Body>
        </Card>
      </Col>
      <Col md={2}>
        <Card className="border-0 shadow-sm">
          <Card.Body className="text-center">
            <div className="text-success mb-2">
              <FiCheck size={20} />
            </div>
            <h5 className="text-success">{summary.by_status?.approved || 0}</h5>
            <small className="text-muted">Approved</small>
          </Card.Body>
        </Card>
      </Col>
      <Col md={2}>
        <Card className="border-0 shadow-sm">
          <Card.Body className="text-center">
            <div className="text-warning mb-2">
              <FiClock size={20} />
            </div>
            <h5 className="text-warning">{summary.by_status?.requested || 0}</h5>
            <small className="text-muted">Pending</small>
          </Card.Body>
        </Card>
      </Col>
      <Col md={2}>
        <Card className="border-0 shadow-sm">
          <Card.Body className="text-center">
            <div className="text-info mb-2">
              <FiTrendingUp size={20} />
            </div>
            <h5 className="text-info">{summary.consolidation_eligible || 0}</h5>
            <small className="text-muted">Consolidatable</small>
          </Card.Body>
        </Card>
      </Col>
      <Col md={2}>
        <Card className="border-0 shadow-sm">
          <Card.Body className="text-center">
            <div className="text-primary mb-2">
              <FiShoppingCart size={20} />
            </div>
            <h5 className="text-primary">{summary.with_rfq || 0}</h5>
            <small className="text-muted">With RFQ</small>
          </Card.Body>
        </Card>
      </Col>
      <Col md={2}>
        <Card className="border-0 shadow-sm">
          <Card.Body className="text-center">
            <div className="text-danger mb-2">
              <FiAlertCircle size={20} />
            </div>
            <h5 className="text-danger">{summary.overdue || 0}</h5>
            <small className="text-muted">Overdue</small>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );

  return (
    <Layout>
      <Container fluid className="py-4">
        {/* Header */}
        <Row className="mb-4">
          <Col>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h2 className="mb-1">Purchase Request Management</h2>
                <p className="text-muted mb-0">
                  Manage purchase requests with consolidation opportunities
                </p>
              </div>
              <div className="d-flex gap-2">
                {selectedRequests.length > 0 && (
                  <Button
                    variant="outline-warning"
                    onClick={() => setShowBulkUpdateModal(true)}
                  >
                    <FiUsers className="me-1" />
                    Bulk Update ({selectedRequests.length})
                  </Button>
                )}
                <Button
                  variant="outline-info"
                  onClick={() => {
                    setShowConsolidationModal(true);
                    fetchConsolidationAnalysis('product');
                  }}
                >
                  <FiTrendingUp className="me-1" />
                  Consolidation Analysis
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={() => setShowFiltersModal(true)}
                >
                  <FiFilter className="me-1" />
                  Filters
                </Button>
                <Button
                  variant="outline-primary"
                  onClick={fetchPurchaseRequests}
                >
                  <FiRefreshCw className="me-1" />
                  Refresh
                </Button>
                <Button
                  variant="primary"
                  onClick={() => navigate('/purchase-requests/create')}
                >
                  <FiPlus className="me-1" />
                  New Request
                </Button>
              </div>
            </div>
          </Col>
        </Row>

        {/* Summary Cards */}
        {renderSummaryCards()}

        {/* Search and Quick Filters */}
        <Row className="mb-3">
          <Col md={6}>
            <InputGroup>
              <InputGroup.Text>
                <FiSearch />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search purchase requests..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </InputGroup>
          </Col>
          <Col md={6} className="text-end">
            {Object.values(filters).some(filter => filter !== '') && (
              <Button variant="outline-secondary" onClick={clearFilters}>
                Clear Filters ({Object.values(filters).filter(f => f !== '').length})
              </Button>
            )}
          </Col>
        </Row>

        {/* Main Content */}
        <Card>
          <Card.Header>
            <ul className="nav nav-tabs card-header-tabs">
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === "all" ? "active" : ""}`}
                  onClick={() => setActiveTab("all")}
                >
                  All ({summary.total_requests || 0})
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === "pending_approval" ? "active" : ""}`}
                  onClick={() => setActiveTab("pending_approval")}
                >
                  Pending Approval
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === "approved" ? "active" : ""}`}
                  onClick={() => setActiveTab("approved")}
                >
                  Approved
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === "consolidation_eligible" ? "active" : ""}`}
                  onClick={() => setActiveTab("consolidation_eligible")}
                >
                  Consolidatable
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === "with_rfq" ? "active" : ""}`}
                  onClick={() => setActiveTab("with_rfq")}
                >
                  With RFQ
                </button>
              </li>
            </ul>
          </Card.Header>
          <Card.Body className="p-0">
            {loading ? (
              <div className="text-center py-4">
                <Spinner animation="border" />
                <p className="mt-2">Loading purchase requests...</p>
              </div>
            ) : (
              renderPurchaseRequestTable()
            )}
          </Card.Body>
        </Card>

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <Row className="mt-3">
            <Col className="d-flex justify-content-center">
              <nav>
                <ul className="pagination">
                  <li className={`page-item ${pagination.current_page === 1 ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page - 1 }))}
                    >
                      Previous
                    </button>
                  </li>
                  {Array.from({ length: pagination.total_pages }, (_, i) => i + 1).map(page => (
                    <li key={page} className={`page-item ${pagination.current_page === page ? 'active' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => setPagination(prev => ({ ...prev, current_page: page }))}
                      >
                        {page}
                      </button>
                    </li>
                  ))}
                  <li className={`page-item ${pagination.current_page === pagination.total_pages ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page + 1 }))}
                    >
                      Next
                    </button>
                  </li>
                </ul>
              </nav>
            </Col>
          </Row>
        )}

        {/* Modals */}
        {renderConsolidationModal()}
        {renderBulkUpdateModal()}
        {renderFiltersModal()}
      </Container>
    </Layout>
  );
};

export default PurchaseRequestManagement;